import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import { cn } from "./utils";

const selectVariants = cva(
  "flex w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-sm transition-all duration-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
  {
    variants: {
      variant: {
        default: "border-neutral-300 focus:border-mebi-yellow-500 focus:ring-2 focus:ring-mebi-yellow-500/20",
        error: "border-mebi-red-500 focus:border-mebi-red-500 focus:ring-2 focus:ring-mebi-red-500/20",
      },      size: {
        sm: "h-9 px-3 text-xs",
        md: "h-10 px-3 text-sm",
        lg: "h-11 px-4 text-base",
        mobile: "h-12 px-4 text-base",
      },
    },    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size">,
    VariantProps<typeof selectVariants> {
  /**
   * Label hiển thị phía trên select
   */
  label?: string;
  
  /**
   * Thông báo lỗi hiển thị phía dưới select
   */
  error?: string;
  
  /**
   * Thông báo hỗ trợ hiển thị phía dưới select
   */
  helperText?: string;
  
  /**
   * Danh sách options
   */
  options: SelectOption[];
  
  /**
   * Placeholder text
   */
  placeholder?: string;
  
  /**
   * Container className
   */
  containerClassName?: string;
  
  /**
   * Label className
   */
  labelClassName?: string;
}

/**
 * Select component - Dropdown select với MEBI Design System
 * 
 * @example
 * ```tsx
 * <Select
 *   label="Thành phố"
 *   placeholder="Chọn thành phố"
 *   options={[
 *     { value: "hcm", label: "TP. Hồ Chí Minh" },
 *     { value: "hn", label: "Hà Nội" }
 *   ]}
 * />
 * ```
 */
const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({
    className,
    variant,
    size,
    label,
    error,
    helperText,
    options,
    placeholder,
    containerClassName,
    labelClassName,
    disabled,
    ...props
  }, ref) => {
    // Generate unique ID using useId hook
    const generatedId = React.useId();
    const selectId = props.id || `select-${generatedId}`;
    
    // Determine variant based on error state
    const selectVariant = error ? "error" : variant;
    
    return (
      <div className={cn("space-y-2", containerClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={selectId}            className={cn(
              "mebi-body-small-semibold text-neutral-1300 block",
              disabled && "text-neutral-600",
              error && "text-mebi-red-600",
              labelClassName
            )}
          >
            {label}
            {props.required && <span className="text-mebi-red-500 ml-1">*</span>}
          </label>
        )}

        {/* Select Container */}
        <div className="relative">
          <select
            id={selectId}
            className={cn(
              selectVariants({ variant: selectVariant, size }),
              "appearance-none cursor-pointer pr-10",
              className
            )}
            ref={ref}
            disabled={disabled}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={
              error ? `${selectId}-error` : helperText ? `${selectId}-help` : undefined
            }
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>

          {/* Dropdown Icon */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <ChevronDown className="h-4 w-4 text-neutral-600" />
          </div>
        </div>

        {/* Helper Text / Error Message */}
        {(error || helperText) && (
          <div
            id={error ? `${selectId}-error` : `${selectId}-help`}            className={cn(
              "mebi-body-xsmall-regular",
              error ? "text-mebi-red-600" : "text-neutral-600"
            )}
            role={error ? "alert" : undefined}
          >
            {error || helperText}
          </div>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";

export { Select };
